import {useState, useCallback, useEffect, useMemo} from 'react';
import produce from 'immer';
import {useSMCSnackbar} from '../../../utils/hooks';
import {useTranslation} from 'react-i18next';
import {setProperty, getErrorCode, isStringI18n, extractI18nString} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes, ErrorCodes} from '../../../api/consts';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {SMCPaths} from '../../../conf/Routes';
import {push} from 'connected-react-router';
import store from '../../../store';
import generalIcon from '../../../assets/images/general.png';
import settingsIcon from '../../../assets/images/settings.png';
import RoleGeneral from './InnerPages/RoleGeneral';
import RoleAdvanced from './InnerPages/RoleAdvanced';
import { RoleGetSchema } from '../../../api/DBSchema/Role';
import {helpContextPageUrl} from '../../../conf/Routes';

export function useRoleEditor(roleId, defaultChosenPageId) {
    const { t } = useTranslation();
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar, enqueueLogoutSnackbar } = useSMCSnackbar();
    const { getErrorMessage, getMessagesArrayByPages } = useSMCErrorHandler();

    const [origRoleObject, setOrigRoleObject] = useState({});
    const [roleObject, setRoleObject] = useState({});

    const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
    const [isInnerPageLoading, setIsInnerPageLoading] = useState(false);
    const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);
    const [errorMessagesByPageList, setErrorMessagesByPageList] = useState(null);

    const [isObjectDirty, setIsObjectDirty] = useState(false);

    const [chosenPageId, setChosenPageId] = useState(defaultChosenPageId || 'general');

    const editorPages = useMemo(() => ([
        { id: RoleGetSchema.pages.general.pageId, title: t('SMP.GENERAL'), icon: generalIcon, pageElement: RoleGeneral, helpId: helpContextPageUrl.roleGeneral },
        { id: RoleGetSchema.pages.advanced.pageId, title: t('SMC_GEN_ADVANCED'), icon: settingsIcon, pageElement: RoleAdvanced, helpId: helpContextPageUrl.roleAdvanced },
    ]), [t]);

    const [pageIdToPageName, pageIdToHelpId] = useMemo(() => {
        const _pageIdToPageName = {};
        const _pageIdToHelpId = {};
        const addPagesData = (pagesRoot) => {
            pagesRoot.forEach(page => {
                if (page.id) {
                    _pageIdToPageName[page.id] = page.title;
                    _pageIdToHelpId[page.id] = page.helpId;
                }
                if (page.children) {
                    addPagesData(page.children);
                }
            })
        }
        addPagesData(editorPages);
        return [_pageIdToPageName, _pageIdToHelpId];
    }, [editorPages]);

    const helpId = useMemo(() => pageIdToHelpId[chosenPageId], [pageIdToHelpId, chosenPageId]);

    const fetchRole = useCallback(async () => {
        setIsPageDataLoaded(false);
        try {
            const fetchRoleResp = await Api.fetchSingleObject(ObjectTypes.ROLE, roleId);
            const role = fetchRoleResp.data.data.role;
            // Validate server response
            if (!role) {
                console.error('Get role response failed: role response returned empty');
                throw new Error(ErrorCodes.SERVER_ERROR);
            }
            setOrigRoleObject(role);
            setRoleObject(role);
            setIsPageLoadFailed(false);
            setIsObjectDirty(false); // Just fetched, not dirty
        } catch (e) {
            const errorCode = getErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                enqueueLogoutSnackbar(t(errorCode));
            } else {
                enqueueErrorSnackbar(t(errorCode));
            }
            setIsPageLoadFailed(true);
        }
        setIsPageDataLoaded(true);
    }, [roleId, enqueueErrorSnackbar, enqueueLogoutSnackbar, t]);

    const setRoleData = useCallback((paramPath, newValue) => {
        setIsObjectDirty(true); // Data changed, object is dirty
        setRoleObject(roleObj => produce(roleObj, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const onPageChange = useCallback((pageId) => {
        setIsInnerPageLoading(true);
        setChosenPageId(pageId);
        Api.verifySingleObject(ObjectTypes.ROLE, roleId, roleObject)
        .then(() => {
            // On no error, clear error message
            setErrorMessagesByPageList(null);
        })
        .catch((error) => {
            const errorMessageArrayByPage = getMessagesArrayByPages(error, pageIdToPageName);
            if (errorMessageArrayByPage) {
                setErrorMessagesByPageList(errorMessageArrayByPage);
            }
        })
        .finally(() => {
            setIsInnerPageLoading(false);
        });
    }, [roleId, getMessagesArrayByPages, roleObject, pageIdToPageName]);

    const saveButtonOnClick = useCallback(() => {
        Api.updateSingleObject(ObjectTypes.ROLE, roleId, roleObject)
        .then(() => {
            setIsPageDataLoaded(false);
            setErrorMessagesByPageList(null);
            fetchRole();
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_SAVED_OK'));
        })
        .catch((error) => {
            const errorMessage = getErrorMessage(error);
            alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [fetchRole, getErrorMessage, roleId, roleObject, enqueueSuccessSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (window.confirm(t('OBJECT_EDITOR_OBJ_DELETE', [roleId] ))) {
            Api.deleteObjects(ObjectTypes.ROLE, [roleId])
            .then(res => {
                const deleteResult = res.data.results[0];
                if (deleteResult.success) {
                    setIsObjectDirty(false); // Set as false to not trigger dirty modal
                    store.dispatch(push(SMCPaths.roles));
                    enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_DELETE_OK', [roleId]));
                } else {
                    const deleteErrorCode = deleteResult.errorCode;
                    let deleteErrorParams;
                    if (deleteResult.errorParams) {
                        deleteErrorParams = deleteResult.errorParams.map(param => (
                            isStringI18n(param)) ? t(extractI18nString(param)) : param
                        );
                    }
                    enqueueErrorSnackbar(t(extractI18nString(deleteErrorCode), deleteErrorParams));
                }
            })
            .catch(err => {
                const errorCode = getErrorMessage(err);
                enqueueErrorSnackbar(t(errorCode));
            });
        }
    }, [roleId, getErrorMessage, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]);

    const revertButtonOnClick = useCallback(() => {
        setRoleObject(origRoleObject);
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
        setErrorMessagesByPageList(null);
    }, [origRoleObject]);

    // Fetch role on loading of page
    useEffect(() => {
        fetchRole();
    }, [fetchRole]);
    
    return {
        roleObject,
        isObjectDirty,
        editorPages,
        helpId,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setRoleData,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        reloadPageData: fetchRole
    };
}